import type { VellumAuthMeResponse } from "@vellum/vellum-api";
import { FeatureFlagProvider } from "@vellum/ui";
import { vellumApiSdk, fetchFeatureFlags } from "./vellumApiClient";
import { AuthContext } from "./authContext";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Keep this module's exports component-only so React Fast Refresh can hot-swap
// the provider in place. The context and hooks live in `./authContext`.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<VellumAuthMeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await vellumApiSdk.getCurrentUser();
      setUser(result.data ?? null);
    } catch {
      setUser(null);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await vellumApiSdk.logout();
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        await refresh();
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      signOut,
    }),
    [user, loading, refresh, signOut],
  );

  // Wrap children in the feature-flag provider so every micro app that mounts
  // AuthProvider gets flags for free, using the shared vellum-api fetcher.
  return (
    <AuthContext.Provider value={value}>
      <FeatureFlagProvider fetchFlags={fetchFeatureFlags}>
        {children}
      </FeatureFlagProvider>
    </AuthContext.Provider>
  );
}
